import { useCallback } from 'react';
import { useRouter } from 'next/router';
import * as routes from 'pages/routes';
import { PlusIcon } from 'public/icons';
import Button from 'UI/Button';
import { useSubscriptionLimitation } from 'resources/company/hooks';
import { useCurrentUser } from 'resources/user/hooks';
import { useDealsByUserId } from 'resources/deal/hooks';
import ModalManager from '@ebay/nice-modal-react';
import SubscribeModal from 'modals/SubscribeModal';
import VerifyEmailModal from 'modals/VerifyEmailModal';
import PropTypes from 'prop-types';


const NewTransactionButton = ({
  iconBefore,
  label,
  className,
  loading,
  disabled,
  clientId, // flow for selected client
  agentId, // flow for selected agent
  onClickCallback,
  ...props
}) => {
  const router = useRouter();
  const { isSubscriptionStatusLoading, isCreateNewTransactionAvailable } = useSubscriptionLimitation();
  const { data: user } = useCurrentUser();
  const { data: deals } = useDealsByUserId({ userId: user?.id }, { enabled: !!user });

  const goToNewTransactionStart = useCallback(() => {
    if (!user?.app_state?.is_email_verified && deals?.length > 0) {
      ModalManager.show(VerifyEmailModal);
      return;
    }

    if (!isCreateNewTransactionAvailable) {
      ModalManager.show(SubscribeModal);
      return;
    }

    router.push({
      pathname: routes.path.newTransactionOptions,
      query: {
        ...(clientId && { clientId }),
        ...(agentId && { agentId }),
      },
    });

    onClickCallback?.();
  }, [user, isCreateNewTransactionAvailable, deals?.length]);

  return (
    <Button
      onClick={goToNewTransactionStart}
      iconBefore={iconBefore}
      width="max-content"
      className={className}
      loading={isSubscriptionStatusLoading || loading}
      disabled={disabled}
      {...props}
    >
      {label}
    </Button>
  );
};

NewTransactionButton.propTypes = {
  iconBefore: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.func,
    PropTypes.elementType,
    PropTypes.oneOf([null, false])
  ]),
  label: PropTypes.string,
  className: PropTypes.string,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  onClickCallback: PropTypes.func,
};

NewTransactionButton.defaultProps = {
  iconBefore: PlusIcon,
  label: 'New Transaction',
  className: undefined,
  loading: false,
  disabled: false,
  onClickCallback: undefined,
};

export default NewTransactionButton;
